import styled from '@emotion/styled'
import { ReactNode } from 'react'
import { useMediaPredicate } from 'react-media-hook'
import { useEstimatedReadingTime } from './_hooks/use-estimated-reading-time'
import { useRelativeDate } from './_hooks/use-relative-date'
import Link from 'next/link'

const Entry = styled(Link)`
  cursor: pointer;

  display: flex;
  flex-direction: row;

  @media (max-width: 800px) {
    flex-direction: column;
    border-bottom: 1px solid ${({ theme }) => theme.onBackgroundLightest};
    border-top: 1px solid ${({ theme }) => theme.onBackgroundLightest};

    padding: 0 0 2rem;
  }

  gap: 2rem;

  color: ${({ theme }) => theme.onBackground};

  transition: all 0.25s ease;

  :hover {
    opacity: 0.85;

    @media (min-width: 800px) {
      scale: 1.02;
    }
  }
`

const EntryInfo = styled.div`
  max-width: 60%;

  @media (max-width: 800px) {
    max-width: 100%;
  }

  h3 {
    font-size: 1.5rem;

    @media (max-width: 800px) {
      font-size: 1.5rem;
    }

    color: ${({ theme }) => theme.onBackground};
  }

  p {
    margin-top: 0.5rem;
    font-size: 1.25rem;

    @media (max-width: 800px) {
      font-size: 1.15rem;
    }

    color: ${({ theme }) => theme.onBackgroundSemiLight};
    line-height: 1.5rem;

    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  span {
    display: block;
    margin-top: 0.75rem;
    font-size: 1rem;
    @media (max-width: 800px) {
      font-size: 0.95rem;
    }

    color: ${({ theme }) => theme.onBackgroundLight};
  }
`

const EntryImage = styled.div`
  .entry-image-container {
    aspect-ratio: 16 / 9;
  }

  .entry-image {
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const LabelContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`

const Label = styled.div`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.onBackgroundSemiLight};
  background-color: ${({ theme }) => theme.onBackgroundLightest};
`

export interface BlogPost {
  authorName: string
  authorAvatarUrl: string
  headerImageUrl: string
  headerImageAlt: string
  publishedAt: Date
  postTitle: string
  metaDescription: string
  tags: string[]
  slug: string
  children: ReactNode
  overviewText: string
}

export const OverviewEntry = (entry: BlogPost) => {
  const isMobile = useMediaPredicate('(max-width: 800px)')

  const relativeDate = useRelativeDate(entry.publishedAt)
  const { minutes } = useEstimatedReadingTime(JSON.stringify(entry.children))

  return (
    <Entry href={'/blogg/' + entry.slug}>
      {isMobile && (
        <EntryImage>
          <div className="entry-image-container">
            <img className="entry-image" src={entry.headerImageUrl} alt={entry.headerImageAlt} />
          </div>
        </EntryImage>
      )}
      <EntryInfo>
        <h3>{entry.postTitle}</h3>
        <p>{entry.overviewText}</p>
        <span>
          Publicerad {relativeDate} · {minutes} min läsning
        </span>
        {!!entry.tags.length && (
          <LabelContainer>
            {entry.tags.map((tag) => (
              <Label key={tag + entry.slug}>{tag}</Label>
            ))}
          </LabelContainer>
        )}
      </EntryInfo>
      {!isMobile && (
        <EntryImage>
          <div className="entry-image-container">
            <img className="entry-image" src={entry.headerImageUrl} alt={entry.headerImageAlt} />
          </div>
        </EntryImage>
      )}
    </Entry>
  )
}
